import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { billing } from '@repletoryreach/sdk-api';
import { useDebounce, useMediaQuery } from '@repletoryreach/sdk-utils/hooks';
import { rowsToCsv, downloadText } from '@repletoryreach/sdk-utils/file';
import { Stack, Card, Group, Button, Text, Badge } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, EmptyState, MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { formatDateTime } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useCredits } from '../../../lib/credits';
import { useT } from '../../../i18n/I18nProvider';
import { LedgerEntryExplain } from '../components/LedgerEntryExplain';
import { sourceKindLabel, maskMsisdn } from '../components/usage-labels';
import '../billing.css';
import { HelpButton } from '../../../app/guides/HelpButton';

/** What an entry was for, as the billing service records it. */
const SOURCE_KINDS = ['broadcast', 'flow', 'agent', 'inbound', 'kb', 'notify', 'media', 'web', 'voice', 'topup', 'auto_topup', 'manual'] as const;

const PAGE_SIZE = 50;

/** Export reads the filtered ledger in pages of the server's largest size, up to a ceiling. */
const EXPORT_PAGE = 500;
const EXPORT_MAX = 20_000;

/** Entry types the service writes. Coarser credit/debit is a worse cut. */
const ENTRY_TYPES = ['topup', 'grant', 'debit', 'refund', 'adjustment', 'plan'] as const;

/** Date ranges people actually ask for. */
const RANGES = ['all', '7d', '30d', '90d'] as const;
type Range = (typeof RANGES)[number];

function rangeStart(range: Range): string | undefined {
  if (range === 'all') return undefined;
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

export default function BillingLedgerPage() {
  const t = useT();
  const credits = useCredits();
  const narrow = useMediaQuery('(max-width: 820px)'); // i18n-ignore: CSS media query

  /*
   * §4 item 14: filter state lived in component state, so a filtered view
   * could be neither linked nor reloaded.
   */
  const [params, setParams] = useSearchParams();
  const entryType = params.get('type') ?? '';
  const sourceKind = params.get('for') ?? '';
  const range = (RANGES as readonly string[]).includes(params.get('range') ?? '')
    ? (params.get('range') as Range)
    : 'all';
  const setParam = (key: string, value: string) => setParams((prev) => {
    const q = new URLSearchParams(prev);
    if (value) q.set(key, value); else q.delete(key);
    return q;
  }, { replace: true });

  const [query, setQuery] = useState('');
  // §4 item 11: search set state on every keystroke and re-filtered 500 rows
  // synchronously.
  const search = useDebounce(query, 250);
  const [page, setPage] = useState(1);
  const [explain, setExplain] = useState<billing.LedgerEntry | null>(null);
  const [exporting, setExporting] = useState(false);

  // The search now runs on the server (description, contact number, SKU), so
  // it covers the whole ledger and the totals follow it.
  const { data, isPending, error } = useQuery({
    queryKey: ['ledger', 'page', entryType, sourceKind, range, search, page],
    queryFn: () => billing.listLedger({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, ...filters() }),
    placeholderData: (prev) => prev
  });

  useEffect(() => { setPage(1); }, [entryType, sourceKind, range, search]);

  /** The filters on screen, as the ledger route takes them. The page and the export ask the same question. */
  function filters() {
    return {
      ...(entryType ? { entry_type: entryType } : {}),
      ...(sourceKind ? { source_kind: sourceKind } : {}),
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(rangeStart(range) ? { from: rangeStart(range) } : {})
    };
  }

  const all = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;
  const currency = all[0]?.currency ?? 'ZAR';
  const rows = all as Indexed<billing.LedgerEntry>[];

  /*
   * The whole filtered ledger, not the page on screen.
   *
   * This exported `all`, the fifty rows being shown, so once a ledger ran past
   * its first page an export quietly left rows out and no longer added up to
   * the balance. It now reads every page the filters match. The date is when
   * the charge happened, which is what the rows on screen are sorted by
   * meaning; a refund written later for an earlier failure keeps its own day.
   */
  async function exportCsv() {
    setExporting(true);
    const entries: billing.LedgerEntry[] = [];
    try {
      for (let offset = 0; offset < EXPORT_MAX; offset += EXPORT_PAGE) {
        const res = await billing.listLedger({ limit: EXPORT_PAGE, offset, ...filters() });
        entries.push(...res.data);
        if (res.data.length < EXPORT_PAGE) break;
      }
    } catch {
      notifyError(t('billing.ledger.exportFailed'), t('nav.billing'));
      setExporting(false);
      return;
    }
    setExporting(false);
    const csv = rowsToCsv(entries.map((e) => ({
      date: e.occurred_at ?? e.created_at,
      type: e.entry_type,
      for: e.source_kind ?? '',
      sku: e.sku_code ?? '',
      description: e.description ?? '',
      units: e.units ?? '',
      unit: e.unit_label ?? '',
      rate_minor: e.rate_minor ?? '',
      contact: e.contact_msisdn ?? '',
      broadcast_id: e.broadcast_id ?? '',
      flow_id: e.flow_id ?? '',
      conversation_id: e.conversation_id ?? '',
      amount_minor: e.amount_minor,
      currency: e.currency
    })));
    downloadText(csv, `ledger-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
    notifySuccess(t('billing.ledger.exported'), t('nav.billing'));
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('billing.ledger.title')}
        description={t('billing.ledger.description')}
        withDivider={false}
        actions={
          <Group gap="xs">
            <HelpButton guide="account" />
            <Button size="sm" variant="default" onClick={exportCsv} loading={exporting} disabled={all.length === 0}>
              {exporting ? t('billing.ledger.exporting') : t('action.export')}
            </Button>
          </Group>
        }
      />

      {error && <Alert color="error" role="alert">{t('billing.ledgerError')}</Alert>}

      {/* Money totals for the whole filtered set, from the server, split the way
          a wallet is read. The old pair was Added and Spent, and a plan's fee is
          a debit while its included credits are a credit, so a Growth
          subscription showed R499 "spent" beside nine credits of real use and
          added up in nobody's head (14 September 2026). With no filter these
          five come to the balance, and the note under them says so; a plan paid by
          card has its payment beside its fee (11 October 2026). */}
      <Group gap="sm" wrap="wrap">
        <MetricCard label={t('billing.ledger.entries')} value={meta?.total ?? 0} loading={isPending} />
        <MetricCard
          label={t('billing.ledger.creditedTotal')}
          hint={t('billing.ledger.addedHint')}
          value={meta ? credits(meta.added_minor, currency) : '–'}
          loading={isPending}
        />
        <MetricCard
          label={t('billing.ledger.refundedTotal')}
          value={meta ? credits(meta.refunded_minor, currency) : '–'}
          loading={isPending}
        />
        <MetricCard
          label={t('billing.ledger.debitedTotal')}
          hint={t('billing.ledger.spentHint')}
          value={meta ? credits(meta.spent_minor, currency) : '–'}
          loading={isPending}
        />
        <MetricCard
          label={t('billing.ledger.planFeesTotal')}
          hint={t('billing.ledger.planFeesHint')}
          value={meta ? credits(meta.plan_fees_minor, currency) : '–'}
          loading={isPending}
        />
        <MetricCard
          label={t('billing.ledger.paidByCardTotal')}
          hint={t('billing.ledger.paidByCardHint')}
          value={meta ? credits(meta.paid_by_card_minor ?? 0, currency) : '–'}
          loading={isPending}
        />
      </Group>
      <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.ledger.totalsNote')}</Text>

      <div className="rr-billing__filters">
        <Select
          aria-label={t('billing.ledger.type')}
          value={entryType}
          onChange={(e) => setParam('type', e.currentTarget.value)}
          data={[
            { value: '', label: t('billing.ledger.type.any') },
            ...ENTRY_TYPES.map((code) => ({ value: code, label: t(`billing.entryType.${code}`, code) }))
          ]}
        />
        <Select
          aria-label={t('billing.ledger.for')}
          value={sourceKind}
          onChange={(e) => setParam('for', e.currentTarget.value)}
          data={[
            { value: '', label: t('billing.ledger.for.any') },
            ...SOURCE_KINDS.map((k) => ({ value: k, label: sourceKindLabel(t, k) }))
          ]}
        />
        <Select
          aria-label={t('billing.ledger.range')}
          value={range}
          onChange={(e) => setParam('range', e.currentTarget.value)}
          data={RANGES.map((r) => ({ value: r, label: t(`billing.ledger.range.${r}`) }))}
        />
        <TextInput
          type="search"
          // §4 item 10: placeholder-only, so it announced nothing.
          aria-label={t('billing.ledger.search')}
          placeholder={t('billing.ledger.search.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
      </div>

      {!isPending && rows.length === 0 ? (
        <Card withBorder>
          {/* §4 item 3: a fresh tenant was told their filter was too narrow
              when they had not filtered. */}
          <EmptyState
            title={entryType || sourceKind || range !== 'all' || search
              ? t('billing.ledger.emptyFiltered')
              : t('billing.noTransactionsYet')}
            description={entryType || sourceKind || range !== 'all' || search
              ? undefined
              : t('billing.noTransactions.hint')}
            action={entryType || sourceKind || range !== 'all' || search
              ? <Button size="xs" variant="default" onClick={() => { setParams({}, { replace: true }); setQuery(''); }}>
                  {t('inbox.filters.clear')}
                </Button>
              : undefined}
          />
        </Card>
      ) : narrow ? (
        <Stack gap="sm">
          {rows.map((e) => (
            <Card key={e.id} withBorder>
              <button type="button" className="rr-billing__entry" onClick={() => setExplain(e)}>
                <Stack gap={1} style={{ minWidth: 0, flex: 1 }}>
                  <Text role="bodySmall" truncate>{e.description ?? t(`billing.entryType.${e.entry_type}`, e.entry_type)}</Text>
                  <Group gap={6}>
                    <Badge size="xs" variant="light">{sourceKindLabel(t, e.source_kind ?? e.entry_type)}</Badge>
                    {e.contact_msisdn && <Text role="caption" c="var(--rr-color-text-muted)">{maskMsisdn(e.contact_msisdn)}</Text>}
                  </Group>
                  <Text role="caption" c="var(--rr-color-text-muted)">{formatDateTime(e.created_at)}</Text>
                </Stack>
                <Amount entry={e} />
              </button>
            </Card>
          ))}
        </Stack>
      ) : (
        <Card withBorder noPadding>
          <DataTable<Indexed<billing.LedgerEntry>>
            records={rows}
            loading={isPending}
            idAccessor="id"
            page={page}
            totalRecords={meta?.total ?? 0}
            recordsPerPage={PAGE_SIZE}
            onPageChange={setPage}
            emptyState={t('billing.noTransactionsYet')}
            columns={[
              { accessor: 'created_at', title: t('common.date'), render: (e) => formatDateTime(e.occurred_at ?? e.created_at) },
              {
                accessor: 'source_kind',
                title: t('billing.ledger.for'),
                // What it was for, in words; the entry type is a coarser cut
                // that the explanation still shows.
                render: (e) => (
                  <Stack gap={0}>
                    <Badge size="xs" variant="light">{sourceKindLabel(t, e.source_kind ?? e.entry_type)}</Badge>
                    {e.contact_msisdn && <Text role="caption" c="var(--rr-color-text-muted)">{maskMsisdn(e.contact_msisdn)}</Text>}
                  </Stack>
                )
              },
              {
                accessor: 'description',
                title: t('common.description'),
                render: (e) => (
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text role="bodySmall">{e.description ?? <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>}</Text>
                    {e.units != null && e.rate_minor != null && e.amount_minor < 0 && (
                      <Text role="caption" c="var(--rr-color-text-muted)">
                        {t('billing.ledger.unitsAtRate', { units: Number(e.units), rate: credits(Number(e.rate_minor), e.currency) })}
                      </Text>
                    )}
                  </Stack>
                )
              },
              { accessor: 'amount_minor', title: t('billing.ledger.amount'), textAlign: 'right', render: (e) => <Amount entry={e} /> },
              {
                accessor: 'actions', title: t('common.actions'), textAlign: 'right',
                render: (e) => <Button size="xs" variant="subtle" onClick={() => setExplain(e)}>{t('billing.ledger.explain')}</Button>
              }
            ]}
          />
        </Card>
      )}

      <LedgerEntryExplain entry={explain} onClose={() => setExplain(null)} />
    </Stack>
  );
}

/** §4 items 2 and 12: every row was formatted in ZAR, and colour was the only signal. */
function Amount({ entry }: { entry: billing.LedgerEntry }) {
  const t = useT();
  const credits = useCredits();
  const debit = entry.amount_minor < 0;
  return (
    <Text role="bodySmall" fw={600} className={`rr-billing__amount rr-billing__amount--${debit ? 'debit' : 'credit'}`}>
      {debit ? '−' : '+'}
      {credits(Math.abs(entry.amount_minor), entry.currency)}
      <span className="rr-visually-hidden">{debit ? t('billing.debit') : t('billing.credit')}</span>
    </Text>
  );
}
